import React, { useState, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Dialog, Message } from '@fx-ui/fine-design';
import { isEmpty, forEach, get, set, post, getApi, getErrorMsg, pick, first } from '~/utils';
import Intl from '@fx-ui/intl';
import { API } from '~/constants';
import ReceiptInput from '../receipt-dialog/ReceiptInput';
import { FileItem, FileUpload } from '~/components';
import { IFile } from '~/interfaces';
import { corpUserStore } from '~/models';
export interface IPayProofInfo {
    id: string;
    order_no: string;
    pay_meta?: any;
}

interface IPayProofDialogProps {
    showPayProofDialog: boolean;
    payProofInfo: IPayProofInfo;
    onCancel: () => void;
    onConfirmBtnClick?: () => void;
    onSubmitSuccess?: () => void;
}

const getProofText = (proofField: string) => {
    switch (proofField) {
        case 'contact_name':
            return Intl.get('contact');
        case 'contact_phone':
            return Intl.get('alliance.profile.phone');
        case 'account_name':
            return Intl.get('order.payment.account.name');
        case 'account_no':
            return Intl.get('order.payment.account.no');
        default:
            return '';
    }
};

const PayProofDialog: React.FunctionComponent<IPayProofDialogProps> = (props) => {
    const { showPayProofDialog, payProofInfo, onCancel, onConfirmBtnClick, onSubmitSuccess } =
        props;
    const [originFile, setOriginFile] = useState<IFile[]>();
    const firstFile = useMemo(() => first(originFile), [originFile]);
    const [formStage, setFormStage] = useState<boolean>(true);
    const [dataValue, setDataValue] = useState<any>({});
    const inputRefList = useRef<any>({});

    useEffect(() => {
        setOriginFile(get(payProofInfo, 'pay_meta.proof_img'));
        setDataValue({
            order_no: payProofInfo.order_no,
            ...pick(payProofInfo.pay_meta, [
                'account_no',
                'account_name',
                'contact_phone',
                'contact_name',
                'proof_img'
            ])
        });
        setFormStage(true);
    }, [payProofInfo]);

    const proofFields = ['contact_name', 'contact_phone', 'account_name', 'account_no'];

    const renderFormContent = () => {
        return (
            <div className="pay-proof-info-form">
                <div className="form-item payProof-order-number">
                    <div className="item-label">{Intl.get('order.no')}</div>
                    <div className="item-info">{payProofInfo.order_no}</div>
                </div>
                <div className="form-item payment-proof">
                    <div className="item-label">{Intl.get('order.payment.proof')}</div>
                    {formStage ? (
                        <div className="payment-proof-content">
                            <FileUpload
                                className="payment-proof-upload"
                                style={{ width: 440 }}
                                uploadTokenUrl={getApi(API.file.get_proof_token)}
                                value={originFile}
                                accept=".jpg,.jpeg,.png"
                                onChange={(files) => {
                                    setOriginFile(files);
                                    changeDataValue('proof_img', files);
                                }}
                                maxSizeTip={Intl.get('upload.maxSizeTip', { size: 5 })}
                            />
                        </div>
                    ) : (
                        <div className="item-info">
                            {firstFile && <FileItem file={firstFile} style={{ width: 440 }} />}
                        </div>
                    )}
                </div>
                {renderInputItems()}
            </div>
        );
    };

    const renderInputItems = () => {
        return (
            <>
                {proofFields.map((proofField: string, index: number) => {
                    return (
                        <div key={index} className={classNames('form-item', proofField)}>
                            <div className="item-label">{Intl.get(getProofText(proofField))}</div>
                            {formStage ? (
                                <ReceiptInput
                                    style={{ width: 440 }}
                                    allowBlank={false}
                                    value={dataValue[proofField]}
                                    inputType={proofField}
                                    ref={(ref) => {
                                        !isEmpty(ref) && set(inputRefList.current, proofField, ref);
                                    }}
                                    onChange={(value: string) => {
                                        changeDataValue(proofField, value);
                                    }}
                                />
                            ) : (
                                <div className="item-info">{dataValue[proofField]}</div>
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    const changeDataValue = (name: string, value: any) => {
        setDataValue({
            ...dataValue,
            [name]: value
        });
    };

    const handleOk = () => {
        if (formStage) {
            if (validateForm()) {
                setFormStage(false);
            } else {
                Message.add({
                    messageType: 'toast',
                    type: 'warning',
                    message: Intl.get('trigger.plugin.required')
                });
            }
        } else {
            onConfirmBtnClick && onConfirmBtnClick();
            return post(getApi(API.order.submit_pay_proof, corpUserStore.tenantId), {
                order_id: payProofInfo.id,
                proof: dataValue
            })
                .then(() => {
                    onSubmitSuccess && onSubmitSuccess();
                    const submitDialog = Dialog.add({
                        className: 'fx-submit-success-dialog pay-proof-submit',
                        title: Intl.get('corp.order.payProof'),
                        separator: { footer: false },
                        content: (
                            <>
                                <div className="submit-success">{Intl.get('submit.success')}</div>
                                <div className="submit-success-tip">
                                    {Intl.get('order.payment.proof.tip')}
                                </div>
                            </>
                        ),
                        text4Ok: null,
                        text4Cancel: null,
                        width: 655,
                        height: 305,
                        onCancel: () => {
                            Dialog.hide(submitDialog);
                        }
                    });
                })
                .catch((error) => {
                    const err = get(error, 'response.data', {});
                    Message.add({
                        messageType: 'toast',
                        message: getErrorMsg(err) || Intl.get('submit.fail'),
                        type: 'error'
                    });
                });
        }
    };

    const validateForm = () => {
        let valid = true;
        forEach(proofFields, (validField: string) => {
            const fieldvalid = get(inputRefList.current, validField)?.validateInputState();
            valid = valid && fieldvalid;
        });
        valid = valid && !isEmpty(get(dataValue, 'proof_img.0'));
        return valid;
    };

    return (
        <Dialog
            className="fx-pay-proof-dialog"
            width={600}
            height={475}
            title={Intl.get('order.payProof.submit')}
            visible={showPayProofDialog}
            separator={{ footer: false }}
            text4Cancel={formStage ? Intl.get('cancel') : Intl.get('returnAndEdit')}
            text4Ok={formStage ? Intl.get('ok') : Intl.get('confirmInfo')}
            onOk={handleOk}
            onCancel={(_e, effect) => {
                if (effect === 'cancel') {
                    if (!formStage) {
                        setFormStage(true);
                    } else {
                        onCancel();
                    }
                }
                if (effect === 'close') {
                    onCancel();
                }
            }}
        >
            {!formStage && <div className="warn-info">{Intl.get('order.payProof.submit.tip')}</div>}
            {renderFormContent()}
        </Dialog>
    );
};

export default PayProofDialog;
